import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
    setMobileMenuOpen(false);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  return (
    <nav className="bg-gradient-to-r from-medical-800 via-medical-700 to-tech-800 text-white shadow-2xl border-b border-medical-600/30 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16 lg:h-18">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2 lg:space-x-3 group" onClick={closeMobileMenu}>
              <div className="w-8 h-8 lg:w-10 lg:h-10 group-hover:scale-110 transition-transform duration-300">
                <img 
                  src="/logo-icon.svg" 
                  alt="SC△NMEDX Logo" 
                  className="w-full h-full object-contain"
                />
              </div>
              <div className="hidden sm:block">
                <div className="font-display text-xl lg:text-2xl font-bold text-white">
                  SC<span className="text-medical-200">△</span>NMEDX
                </div>
                <div className="text-xs text-medical-200 -mt-1">Medical Imaging Solutions</div>
              </div>
              <div className="sm:hidden">
                <div className="font-display text-lg font-bold text-white">
                  SC<span className="text-medical-200">△</span>NMEDX
                </div>
              </div>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-6">
            <Link 
              to="/courses" 
              className="flex items-center space-x-2 px-4 py-2 rounded-xl hover:bg-white/10 transition-all duration-300 hover:scale-105 group"
            >
              <svg className="w-4 h-4 group-hover:text-medical-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <span className="font-medium">Courses</span>
            </Link>
            
            {user ? (
              <>
                <Link 
                  to="/my-courses" 
                  className="flex items-center space-x-2 px-4 py-2 rounded-xl hover:bg-white/10 transition-all duration-300 hover:scale-105 group"
                >
                  <svg className="w-4 h-4 group-hover:text-medical-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium">My Courses</span>
                </Link>
                
                {(user.is_instructor || user.is_admin || user.role === 'admin' || user.role === 'super_admin') && (
                  <Link 
                    to="/instructor" 
                    className="flex items-center space-x-2 px-4 py-2 rounded-xl hover:bg-white/10 transition-all duration-300 hover:scale-105 group"
                  >
                    <svg className="w-4 h-4 group-hover:text-medical-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span className="font-medium">
                      {user.is_instructor && !user.is_admin ? 'Instructor' : 'Course Management'}
                    </span>
                  </Link>
                )}
                
                {(user.is_admin || user.role === 'admin' || user.role === 'super_admin') && (
                  <Link 
                    to="/admin" 
                    className="flex items-center space-x-2 px-4 py-2 rounded-xl hover:bg-white/10 transition-all duration-300 hover:scale-105 group"
                  >
                    <svg className="w-4 h-4 group-hover:text-medical-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span className="font-medium">Admin Dashboard</span>
                  </Link>
                )}
                
                <div className="flex items-center space-x-4 pl-4 border-l border-white/20">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-gradient-to-br from-medical-400 to-tech-500 rounded-xl flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-white">Dr. {user.full_name}</div>
                      <div className="text-xs text-medical-200">Medical Professional</div>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="bg-gradient-to-r from-tech-600 to-tech-700 hover:from-tech-700 hover:to-tech-800 px-4 py-2 rounded-xl font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-3">
                <Link
                  to="/login"
                  className="bg-gradient-to-r from-medical-500 to-medical-600 hover:from-medical-600 hover:to-medical-700 px-5 py-2 rounded-xl font-medium transition-all duration-300 hover:scale-105 hover:shadow-lg"
                >
                  Login
                </Link>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="lg:hidden flex items-center">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded-xl hover:bg-white/10 transition-colors"
              aria-label="Toggle mobile menu"
            >
              <svg 
                className={`w-6 h-6 transition-transform duration-300 ${mobileMenuOpen ? 'rotate-45' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                {mobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div className={`lg:hidden transition-all duration-300 ease-in-out ${
          mobileMenuOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
        }`}>
          <div className="px-2 pt-2 pb-6 space-y-2 border-t border-white/20 mt-4">
            <Link 
              to="/courses" 
              onClick={closeMobileMenu}
              className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-white/10 transition-all duration-300 group"
            >
              <svg className="w-5 h-5 group-hover:text-medical-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <span className="font-medium text-base">Courses</span>
            </Link>
            
            {user ? (
              <>
                <Link 
                  to="/my-courses" 
                  onClick={closeMobileMenu}
                  className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-white/10 transition-all duration-300 group"
                >
                  <svg className="w-5 h-5 group-hover:text-medical-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="font-medium text-base">My Courses</span>
                </Link>
                
                {(user.is_instructor || user.is_admin || user.role === 'admin' || user.role === 'super_admin') && (
                  <Link 
                    to="/instructor" 
                    onClick={closeMobileMenu}
                    className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-white/10 transition-all duration-300 group"
                  >
                    <svg className="w-5 h-5 group-hover:text-medical-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                    </svg>
                    <span className="font-medium text-base">
                      {user.is_instructor && !user.is_admin ? 'Instructor' : 'Course Management'}
                    </span>
                  </Link>
                )}
                
                {(user.is_admin || user.role === 'admin' || user.role === 'super_admin') && (
                  <Link 
                    to="/admin" 
                    onClick={closeMobileMenu}
                    className="flex items-center space-x-3 px-4 py-3 rounded-xl hover:bg-white/10 transition-all duration-300 group"
                  >
                    <svg className="w-5 h-5 group-hover:text-medical-200" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2-2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <span className="font-medium text-base">Admin Dashboard</span>
                  </Link>
                )}
                
                {/* User Profile Section */}
                <div className="px-4 py-3 border-t border-white/20 mt-4">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-gradient-to-br from-medical-400 to-tech-500 rounded-xl flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    </div>
                    <div>
                      <div className="text-base font-medium text-white">Dr. {user.full_name}</div>
                      <div className="text-sm text-medical-200">Medical Professional</div>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full bg-gradient-to-r from-tech-600 to-tech-700 hover:from-tech-700 hover:to-tech-800 px-4 py-3 rounded-xl font-medium transition-all duration-300 text-center"
                  >
                    Logout
                  </button>
                </div>
              </>
            ) : (
              <div className="px-4 py-3 border-t border-white/20 mt-4">
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="block w-full bg-gradient-to-r from-medical-500 to-medical-600 hover:from-medical-600 hover:to-medical-700 px-4 py-3 rounded-xl font-medium transition-all duration-300 text-center"
                >
                  Login
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar; 